import React from 'react';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { mapToSelectOptions } from 'utils/select';
import UserRoleType from 'types/enums/UserRoleType';
import { StockHoldingGainLossFilters, StockHoldingGainLossResponse } from 'types/models/StockHoldingGainLoss';
import Venue from 'types/models/Venue';

import useSetupInitialState from 'hooks/useSetupInitialState';
import AppContext from 'contexts/AppContext';
import SelectField from 'components/SelectField';
import SaveError from 'components/common/SaveError';
import { getStockHoldingGainLoss, exportStockHoldingGainLoss } from 'services/stockHoldingGainLossServices';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { StockHoldingGainLossForm } from 'forms/schemas/StockHoldingGainLossForm';
import { getApiErrorMessage } from 'utils/errors';
import { formatMoneyAuto } from 'utils/formatMoney';
import 'styles/pages/StockHoldingGainLossPage.scss';

const PER_PAGE = 10;

interface HoldingFiltersFormValues {
  venueId: string;
  searchTerm: string;
  type: string[];
  isByDate: boolean;
  dateTo: string;
}

const HoldingGainLossReportForm = ({
  onSearch,
  onExport,
  isLoading,
  isExporting,
}: {
  onSearch: (filters: StockHoldingGainLossFilters) => void;
  onExport: (filters: StockHoldingGainLossFilters) => void;
  isLoading: boolean;
  isExporting: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isAdmin = authData?.user?.role === UserRoleType.Admin;

  const [venues, setVenues] = React.useState<Venue[] | null>(null);
  const [loadingFilters, setLoadingFilters] = React.useState(false);

  const typeFilterOptions = [
    { label: t('stock.holding.type-gain'), value: 'gain' },
    { label: t('stock.holding.type-loss'), value: 'loss' },
  ];

  const getEffectiveFilters = (values: HoldingFiltersFormValues): StockHoldingGainLossFilters => ({
    venueId: values.venueId,
    searchTerm: values.searchTerm || undefined,
    type: values.type.length ? values.type.join(',') : undefined,
    dateTo: values.isByDate ? values.dateTo || undefined : undefined,
  });

  const formik = useFormik<HoldingFiltersFormValues>({
    enableReinitialize: true,
    initialValues: {
      venueId: entity?.id ?? '',
      searchTerm: '',
      type: [],
      isByDate: false,
      dateTo: '',
    },
    validationSchema: StockHoldingGainLossForm(t),
    onSubmit: (values) => {
      onSearch(getEffectiveFilters(values));
    },
  });

  React.useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    setLoadingFilters(true);
    const loadVenues = async () => {
      try {
        const response = await getVenuesWithStockModuleActive();
        if (mounted) setVenues(response?.data ?? []);
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    };
    loadVenues();
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  const filterDisabled = isLoading || loadingFilters || !formik.values.venueId;

  return (
    <>
      {isAdmin && (
        <SelectField
          label={t('stock.holding.select-venue')}
          name="venueId"
          placeholder={t('stock.profitability.location-venue-placeholder')}
          value={formik.values.venueId}
          options={mapToSelectOptions(venues ?? [], { label: 'name' })}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.venueId}
          error={formik.errors.venueId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={isLoading}
          isSearchable
        />
      )}
      <SelectField
        label={t('stock.holding.filter-type')}
        name="type"
        placeholder={t('stock.holding.filter-type-placeholder')}
        value={formik.values.type}
        options={typeFilterOptions}
        isMulti
        setFieldValue={formik.setFieldValue}
        onBlur={formik.handleBlur}
        touched={formik.touched.type}
        error={formik.errors.type}
        isClearable={false}
        backspaceRemovesValue={false}
        isDisabled={filterDisabled}
        isSearchable={false}
      />
      <FormControlLabel
        className="holding-by-date"
        control={
          <Checkbox
            name="isByDate"
            checked={formik.values.isByDate}
            onChange={formik.handleChange}
            color="primary"
            disabled={filterDisabled}
          />
        }
        label={t('stock.holding.by-date')}
        title={t('stock.holding.by-date-title')}
      />
      <TextField
        name="dateTo"
        label={t('stock.holding.date-to-label')}
        type="date"
        value={formik.values.dateTo}
        onChange={formik.handleChange}
        InputLabelProps={{ shrink: true }}
        disabled={filterDisabled || !formik.values.isByDate}
        error={formik.touched.dateTo && Boolean(formik.errors.dateTo)}
        helperText={
          formik.touched.dateTo && formik.errors.dateTo
            ? formik.errors.dateTo
            : formik.values.isByDate
            ? t('stock.holding.by-date-hint')
            : undefined
        }
        fullWidth
      />
      <TextField
        name="searchTerm"
        label={t('stock.holding.search')}
        value={formik.values.searchTerm}
        onChange={formik.handleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') formik.handleSubmit();
        }}
        fullWidth
        disabled={filterDisabled}
      />

      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => formik.handleSubmit()}
        disabled={filterDisabled}
      >
        {t('stock.holding.search-button')}
      </Button>

      <Button
        className="btn-50"
        variant="outlined"
        color="primary"
        disabled={filterDisabled || isExporting}
        onClick={() => onExport(getEffectiveFilters(formik.values))}
      >
        {isExporting ? t('loading') : t('stock.holding.export-excel')}
      </Button>
    </>
  );
};

export default (): JSX.Element => {
  const { t } = useTranslation();

  const [filters, setFilters] = React.useState<StockHoldingGainLossFilters | null>(null);
  const [response, setResponse] = React.useState<StockHoldingGainLossResponse>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadData = React.useCallback(async (searchFilters: StockHoldingGainLossFilters, page: number) => {
    setIsLoading(true);
    try {
      const data = await getStockHoldingGainLoss(searchFilters, page, PER_PAGE);
      setResponse(data);
    } catch (err) {
      console.error(err);
      setError(getApiErrorMessage(err) ?? t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (filters) {
      loadData(filters, 1);
    }
  }, [filters, loadData]);

  const handlePageChange = (page: number) => {
    if (!filters) return;
    loadData(filters, page);
  };

  const handleExport = async (exportFilters: StockHoldingGainLossFilters) => {
    if (!exportFilters.venueId || isExporting) return;

    setIsExporting(true);
    try {
      const blob = await exportStockHoldingGainLoss(exportFilters);
      if (!blob) {
        console.error('Export failed');
        return;
      }

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      a.download = 'tenencia_stock.xlsx';
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const hasSearched = filters !== null;

  const variationLabel = (type: string) => {
    if (type === 'gain') return t('stock.holding.type-gain');
    if (type === 'loss') return t('stock.holding.type-loss');
    return '—';
  };

  return (
    <>
      <HoldingGainLossReportForm
        onSearch={setFilters}
        onExport={handleExport}
        isLoading={isLoading}
        isExporting={isExporting}
      />

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress />
        </div>
      )}
      <SaveError error={error} />
      {hasSearched && !isLoading && response && (
        <>
          <Box className="holding-explain">
            <Typography variant="body2" component="div" className="holding-explain__body">
              {t('stock.holding.how-calculated')}
            </Typography>
            <Typography variant="body2" component="div" className="holding-explain__example">
              {t('stock.holding.how-calculated-example')}
            </Typography>
          </Box>

          {filters?.dateTo && (
            <Box className="holding-by-date-note">
              <Typography variant="body2" component="div">
                {t('stock.holding.by-date-note')}
              </Typography>
            </Box>
          )}

          <TableContainer className="table-container" component={Paper}>
            <Table>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell>{t('stock.holding.table.product')}</TableCell>
                  <TableCell>{t('stock.holding.table.sku')}</TableCell>
                  <TableCell align="center">{t('stock.holding.table.stock')}</TableCell>
                  <TableCell align="right">{t('stock.holding.table.avg-cost')}</TableCell>
                  <TableCell align="right">{t('stock.holding.table.replacement-cost')}</TableCell>
                  <TableCell align="right">{t('stock.holding.table.historical-value')}</TableCell>
                  <TableCell align="right">{t('stock.holding.table.replacement-value')}</TableCell>
                  <TableCell align="right">{t('stock.holding.table.variation')}</TableCell>
                  <TableCell align="center">{t('stock.holding.table.type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {response.products && response.products.length > 0 ? (
                  response.products.map((row) => (
                    <TableRow key={row.globalProductId}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell className="unique-value-cell">{row.sku}</TableCell>
                      <TableCell align="center">{row.totalStock}</TableCell>
                      <TableCell align="right">{formatMoneyAuto(row.averageCost)}</TableCell>
                      <TableCell align="right">{formatMoneyAuto(row.replacementCost)}</TableCell>
                      <TableCell align="right">{formatMoneyAuto(row.historicalValue)}</TableCell>
                      <TableCell align="right">{formatMoneyAuto(row.replacementValue)}</TableCell>
                      <TableCell
                        align="right"
                        style={{
                          color: row.type === 'gain' ? '#2e7d32' : row.type === 'loss' ? '#c62828' : undefined,
                        }}
                      >
                        {formatMoneyAuto(row.variation)}
                      </TableCell>
                      <TableCell align="center">
                        {row.type === 'gain' ? (
                          <Chip size="small" color="primary" label={variationLabel(row.type)} />
                        ) : row.type === 'loss' ? (
                          <Chip size="small" color="secondary" label={variationLabel(row.type)} />
                        ) : (
                          <span>—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9}>{t('stock.holding.no-results')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
              {response.totals && (
                <tfoot>
                  <TableRow className="holding-totals">
                    <TableCell colSpan={4}>
                      {t('stock.holding.totals')} ·{' '}
                      {t('stock.holding.gain-count', { count: response.totals.gainCount })} /{' '}
                      {t('stock.holding.loss-count', { count: response.totals.lossCount })}
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right">{formatMoneyAuto(response.totals.historicalValue)}</TableCell>
                    <TableCell align="right">{formatMoneyAuto(response.totals.replacementValue)}</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color: response.totals.variation >= 0 ? '#2e7d32' : '#c62828',
                        fontWeight: 'bold',
                      }}
                    >
                      {formatMoneyAuto(response.totals.variation)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={response.pagination.total}
            page={response.pagination.currentPage - 1}
            onPageChange={(_, nextPage: number) => {
              handlePageChange(nextPage + 1);
            }}
            rowsPerPage={response.pagination.perPage}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => {
              return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
            }}
          />
        </>
      )}
    </>
  );
};
